import {
  Alert,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import * as R from "ramda";

import { hasScoreWarning, ScoreBar } from "../../../components/ScoreBar";
import Sparkline from "../../../components/Sparkline";
import {
  TrendIcon
} from "../../../components/TrendIcon";
import { useSparklineData } from "../../../data/hooks/usePerformanceAnalysis";
import { AggregatedLensPerformanceStatsDataByLens } from "../../../data/queries/performanceAnalysis";
import { formatTrendPercentage } from "../../../utils/number";
import { CONVERSATION_LENS } from "../../../utils/terminology";
import NoBestPerformerWarning from "./NoBestPerformerWarning";
import VsNumbers from "./VsNumbers";

interface LensAnalysisTableProps {
  data: AggregatedLensPerformanceStatsDataByLens[];
  isLoading: boolean;
  error: Error | null;
  userFilter?: string;
  lensFilter?: string;
  permittedEmails?: string[] | null;
  onClick?: (value: string) => void;
}

const LensAnalysisTable = ({
  data,
  isLoading,
  error,
  userFilter = "all",
  lensFilter = "all",
  permittedEmails = null,
  onClick
}: LensAnalysisTableProps) => {
  const {
    data: sparklineData,
    isLoading: isLoadingSparklines
  } = useSparklineData({
    userFilter,
    lensFilter,
    permittedEmails,
    enabled: !isLoading && !!data && data.length > 0
  });

  const onLensClick = (lensName: string) => {
    if (onClick) {
      onClick(lensName);
    }
  };

  if (isLoading) {
    return (
      <Paper sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load {CONVERSATION_LENS} performance: {error.message}
        </Alert>
      </Paper>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Lens Performance</Typography>
        <Typography variant="body2" color="text.secondary">
          No lens performance data available for the selected filters.
        </Typography>
      </Paper>
    );
  }

  const sortedData = R.sortWith([
    R.descend(R.prop("scored_count")),
    R.ascend(R.prop("lens_name"))
  ], data);

  return (
    <Paper sx={{ mt: 3 }}>
      <Box sx={{ p: 2 }}>
        <Typography variant="h6">{CONVERSATION_LENS} Performance</Typography>
        <Typography variant="body2" color="text.secondary">
          Performance metrics for each {CONVERSATION_LENS} based on selected filters
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ minWidth: 320, color: "text.secondary" }}>{CONVERSATION_LENS}</TableCell>
              <TableCell sx={{ width: 300, minWidth: 250, color: "text.secondary" }}>Performance</TableCell>
              <TableCell align="center" sx={{ width: 100, color: "text.secondary" }}>vs Team</TableCell>
              <TableCell align="center" sx={{ width: 100, color: "text.secondary" }}>vs Best</TableCell>
              <TableCell sx={{ width: 100, minWidth: 100, color: "text.secondary" }}>Trend</TableCell>
              <TableCell sx={{ width: 100, color: "text.secondary" }}>90-Day Trend</TableCell>
              <TableCell align="right" sx={{ width: 100, color: "text.secondary" }}># Scored</TableCell>
              <TableCell align="right" sx={{ width: 110, color: "text.secondary" }}># Not Scored</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedData.map((lens) => {
              const lensSparklineData = sparklineData?.byLens?.find(
                d => d.lens_name === lens.lens_name
              )?.scores || [];
              const showWarning = hasScoreWarning(lens.scored_count, lens.stddev_score);

              return (
                <TableRow
                  key={lens.lens_name}
                  hover
                  onClick={() => onLensClick(lens.lens_name)}
                  sx={{
                    backgroundColor: showWarning ? "#f8f0ea" : "inherit"
                  }}
                >
                  <TableCell>
                    {lens.lens_name}
                  </TableCell>

                  <TableCell>
                    <ScoreBar
                      showRange={true}
                      scoreRange={[0, 5]}
                      minScore={lens.min_score}
                      maxScore={lens.max_score}
                      score={lens.avg_score || 0}
                      overlayScores={[
                        { score: lens.avg_score, label: `${CONVERSATION_LENS} Average` }
                      ]}
                      overlayTopScores={lens.best_user_email ? [
                        { score: lens.best_user_avg_score, label: `Best User: ${lens.best_user_email}` }
                      ] : []}
                      count={lens.scored_count}
                      stddev={lens.stddev_score}
                      showConfidenceInterval={true}
                    />
                  </TableCell>

                  <TableCell align="center">
                    <VsNumbers
                      percentageDelta={lens.pct_delta_vs_group_avg}
                      absoluteDelta={lens.delta_vs_group_avg}
                    />
                  </TableCell>

                  <TableCell align="center">
                    {lens.best_user_email ? (
                      <VsNumbers
                        percentageDelta={lens.pct_delta_vs_best}
                        absoluteDelta={lens.delta_vs_best}
                      />
                    ) : (
                      <NoBestPerformerWarning message={`Not enough data to determine best performer for this ${CONVERSATION_LENS}`} />
                    )}
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {lens.pct_delta_vs_prev_avg && <TrendIcon trendPercentage={lens.pct_delta_vs_prev_avg} />}
                      <Typography
                        variant="body2"
                        sx={{
                          color: lens.pct_delta_vs_prev_avg > 0 ? "success.main" :
                            lens.pct_delta_vs_prev_avg < 0 ? "error.main" : "text.secondary"
                        }}
                      >
                        {formatTrendPercentage(lens.pct_delta_vs_prev_avg)}
                      </Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Sparkline
                      data={lensSparklineData}
                      isLoading={isLoadingSparklines}
                      width={120}
                      height={24}
                      color="#1976d2"
                    />
                  </TableCell>

                  <TableCell align="right">
                    {lens.scored_count.toLocaleString()}
                  </TableCell>

                  <TableCell align="right">
                    {lens.not_scored_count.toLocaleString()}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default LensAnalysisTable;
